import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Stack, Chip, Button, IconButton,
  CircularProgress, Paper, Tooltip,
} from '@mui/material';
import {
  WarningAmberRounded as WarningIcon,
  Close as CloseIcon,
  Medication as MedicationIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { pharmacyAttentionPending, pharmacyAttentionResolve } from '../BaseURL';

// System teal — same palette used by Compliance / Procurement.
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const WARN         = '#b45309';
const WARN_BG      = 'rgba(234,179,8,0.10)';
const WARN_BORDER  = 'rgba(234,179,8,0.35)';
const DANGER       = '#b91c1c';
const DANGER_BG    = 'rgba(220,38,38,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX     = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

/**
 * Pharmacy attention reminder. Fetched on mount + on every refresh tick.
 * Lists every patient the current pharmacist opened but didn't fully
 * disposition (some medications still have Serve = '0').
 *
 * Props:
 *   pharmacist        the current user's username — required
 *   open              external open flag (e.g. forced after login)
 *   onClose           caller can close; reappears on next refresh if still pending
 *   onResolvePatient  (patient) => void — caller jumps to that patient's
 *                                          prescription panel inside the page
 *   refreshKey        bump to force a reload
 */
export default function PharmacyAttentionDialog({
  pharmacist,
  open: forcedOpen,
  onClose,
  onResolvePatient,
  refreshKey = 0,
}) {
  const [open, setOpen]       = useState(false);
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!pharmacist) return [];
    setLoading(true);
    try {
      const { data } = await axios.get(pharmacyAttentionPending, {
        params: { pharmacist },
        timeout: 15000,
      });
      const results = (data?.success && Array.isArray(data.results)) ? data.results : [];
      setList(results);
      return results;
    } catch (err) {
      console.error('pharmacy attention pending failed:', err.message || err);
      setList([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [pharmacist]);

  // Open automatically whenever the backend says there's something pending.
  // Fires on:
  //   • initial mount (i.e. fresh login or pharmacy panel selection)
  //   • every refreshKey bump (after a serve / not-served / dismiss)
  //   • a recurring 10-minute reminder timer while pending work remains
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await fetchPending();
      if (cancelled) return;
      if (results.length > 0) setOpen(true);
    })();
    return () => { cancelled = true; };
  }, [fetchPending, refreshKey]);

  // 10-minute nag interval. We re-open the dialog every cycle if there is
  // still pending work, regardless of whether the user dismissed it last
  // time. Cleared on unmount.
  useEffect(() => {
    if (!pharmacist) return;
    const REMINDER_MS = 10 * 60 * 1000;
    const id = setInterval(async () => {
      const results = await fetchPending();
      if (results.length > 0) setOpen(true);
    }, REMINDER_MS);
    return () => clearInterval(id);
  }, [pharmacist, fetchPending]);

  // External force-open path.
  useEffect(() => {
    if (forcedOpen) setOpen(true);
  }, [forcedOpen]);

  const handleClose = () => {
    setOpen(false);
    onClose && onClose();
  };

  const handleManualResolve = async (row) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Resolve without serving?',
      html:
        `<p style="text-align:left;color:#475569;font-size:13px;">
          You're about to mark <strong>${row.patient_name || row.patient_id}</strong>'s
          prescription as <strong>resolved</strong> without serving the remaining
          <strong>${row.pending_count}</strong> medication${row.pending_count === 1 ? '' : 's'}.
        </p>
        <p style="text-align:left;color:#475569;font-size:13px;">
          Use this only when you've handled the patient outside the system. The recommended path is
          to <strong>Serve</strong> or <strong>Mark Not Served</strong> on each medication.
        </p>`,
      showCancelButton: true,
      confirmButtonText: 'Mark resolved',
      confirmButtonColor: PRIMARY,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(pharmacyAttentionResolve, {
        pharmacist,
        patientId: row.patient_id,
        visitDate: row.visit_date,
      });
      await fetchPending();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not resolve', text: err.message || 'Try again' });
    }
  };

  const handleOpenPatient = (row) => {
    if (typeof onResolvePatient === 'function') {
      onResolvePatient(row);
      setOpen(false);
    }
  };

  if (!pharmacist) return null;

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // Discourage casual dismissal — backdrop / ESC don't close. Only
        // the explicit "Close" button (or onClose from caller) does.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2, py: 1.5,
        bgcolor: WARN_BG, color: WARN,
        borderBottom: `1px solid ${WARN_BORDER}`,
      }}>
        <WarningIcon sx={{ fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...LABEL_SX, color: WARN }}>
            Pharmacy · Pending Attention
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: WARN, lineHeight: 1.2 }}>
            {list.length} patient{list.length === 1 ? '' : 's'} need{list.length === 1 ? 's' : ''} your action
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', color: WARN, opacity: 0.85, mt: 0.25 }}>
            Each medication must be either <strong>Served</strong> or <strong>Marked Not Served</strong> with a reason before the patient is closed.
          </Typography>
        </Box>
        <Tooltip title="Refresh" arrow>
          <IconButton size="small" onClick={fetchPending} sx={{ color: WARN }} disabled={loading}>
            {loading ? <CircularProgress size={14} sx={{ color: WARN }} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
        {list.length === 0 && !loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 36, color: PRIMARY, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.95rem' }}>
              All clear — nothing pending.
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.5 }}>
              Every patient you opened has been fully dispositioned.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {list.map((row) => (
              <Paper
                key={row.id}
                elevation={0}
                sx={{
                  p: 1.25, borderRadius: 1.5,
                  border: `1px solid ${BORDER}`, bgcolor: '#fff',
                  display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: STRONG }}>
                    {row.patient_name?.trim() || row.patient_id}
                    <Typography component="span" sx={{ fontSize: '0.74rem', color: MUTED, ml: 1, fontWeight: 500 }}>
                      #{row.patient_id}
                    </Typography>
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                      Visit: <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>
                        {row.visit_date}
                      </Typography>
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                      Last opened: <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>
                        {row.last_seen_at || row.opened_at}
                      </Typography>
                    </Typography>
                  </Stack>
                </Box>

                <Chip
                  size="small"
                  icon={<MedicationIcon sx={{ fontSize: 14 }} />}
                  label={`${row.pending_count} / ${row.total_count} pending`}
                  sx={{
                    height: 24, fontSize: '0.72rem', fontWeight: 700,
                    bgcolor: DANGER_BG, color: DANGER,
                    border: `1px solid rgba(220,38,38,0.3)`,
                    '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                  }}
                />

                <Stack direction="row" spacing={0.75}>
                  <Tooltip title="Open this patient's prescriptions" arrow>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleOpenPatient(row)}
                      sx={{
                        textTransform: 'none', fontSize: '0.74rem', fontWeight: 700,
                        bgcolor: PRIMARY, color: '#fff',
                        '&:hover': { bgcolor: PRIMARY_DARK },
                      }}
                    >
                      Open
                    </Button>
                  </Tooltip>
                  <Tooltip title="Mark as resolved without serving (rare)" arrow>
                    <Button
                      size="small"
                      onClick={() => handleManualResolve(row)}
                      sx={{
                        textTransform: 'none', fontSize: '0.74rem', fontWeight: 600,
                        color: MUTED, borderColor: BORDER,
                      }}
                      variant="outlined"
                    >
                      Mark resolved
                    </Button>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: '0.72rem', color: MUTED, fontStyle: 'italic', mr: 'auto' }}>
          Closing this dialog won't clear the list — it returns on next sign-in.
        </Typography>
        <Button
          onClick={handleClose}
          sx={{ textTransform: 'none', color: MUTED }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
